/**
 * Weight Graph Modal - Coach Dashboard Weight Visualization
 *
 * Reusable modal for displaying weight data in two modes:
 * - Daily: Weight progression day-by-day
 * - Weekly: Weekly average weights over time
 *
 * Uses the modular MetricChart system
 */

import React, { useState, useEffect } from 'react';
import { X, TrendingDown, Calendar } from 'lucide-react';
import { MetricChart, MetricChartConfig, ChartDataPoint } from '../shared/charts';

interface WeightDataPoint {
  date: string;
  weight: number;
  week_key?: string; // For weekly mode (e.g., "2025-W41")
  week_status?: 'green' | 'red' | 'baseline' | 'missing'; // For weekly mode color coding
  week_change?: number | null; // For weekly mode tooltip
  daily_change?: number | null; // For daily mode day-over-day change
  daily_status?: 'green' | 'red' | null; // For daily mode color coding
}

interface WeightGraphModalProps {
  clientId: string;
  clientName: string;
  mode: 'daily' | 'weekly';
  isOpen: boolean;
  onClose: () => void;
}

type DateRange = '7' | '14' | '30' | '90' | '180' | '365' | 'all' | 'custom';

export const WeightGraphModal: React.FC<WeightGraphModalProps> = ({
  clientId,
  clientName,
  mode,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weightData, setWeightData] = useState<WeightDataPoint[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>('90');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const abortControllerRef = React.useRef<AbortController | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchWeightData();
    }
    return () => {
      // Cancel in-flight request when deps change or unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen, mode, dateRange, customStartDate, customEndDate]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchWeightData = async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      // Build endpoint URL based on mode
      let url = `/api/coach/clients/${clientId}/weight-history?mode=${mode}`;

      // Add date range parameters
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
      } else if (dateRange !== 'all') {
        url += `&days=${dateRange}`;
      }

      const response = await fetch(url, {
        headers: getAuthHeaders(),
        signal: controller.signal
      });

      if (response.ok) {
        const data = await response.json();
        setWeightData(data.history || []);
      } else {
        throw new Error('Failed to fetch weight history');
      }
    } catch (err: unknown) {
      // Don't set error state for aborted requests
      if (err instanceof Error && err.name === 'AbortError') return;
      console.error('Error fetching weight history:', err);
      setError('Failed to load weight data');
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  // Transform weight data to chart format
  const transformWeightDataToChartData = (): ChartDataPoint[] => {
    return weightData.map((point) => {
      // For weekly mode, use week_key as label; for daily mode, use date
      const label = mode === 'weekly' && point.week_key
        ? point.week_key
        : new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      // Determine if point was successful (green) based on mode
      let metTarget = null;
      if (mode === 'weekly' && point.week_status) {
        metTarget = point.week_status === 'green' || point.week_status === 'baseline';
      } else if (mode === 'daily' && point.daily_status) {
        metTarget = point.daily_status === 'green';
      }

      return {
        label,
        value: point.weight,
        metTarget, // This will color the point green/red
        metadata: {
          date: point.date,
          week_key: point.week_key,
          week_status: point.week_status,
          week_change: point.week_change,
          daily_change: point.daily_change,
          daily_status: point.daily_status
        }
      };
    });
  };

  // Calculate statistics
  const calculateStats = () => {
    if (weightData.length === 0) return null;

    const weights = weightData.map(d => d.weight);
    const startWeight = weights[0];
    const currentWeight = weights[weights.length - 1];
    const totalChange = currentWeight - startWeight;
    const minWeight = Math.min(...weights);
    const maxWeight = Math.max(...weights);

    return {
      startWeight,
      currentWeight,
      totalChange,
      minWeight,
      maxWeight,
      dataPoints: weights.length
    };
  };

  // Build chart configuration using MetricChartConfig
  const buildChartConfig = (): MetricChartConfig => {
    return {
      type: 'line',
      datasets: [
        {
          label: mode === 'daily' ? 'Daily Weight' : 'Weekly Average Weight',
          data: transformWeightDataToChartData(),
          color: '#3b82f6', // Blue
          showPoints: true,
          tension: 0.3,
        }
      ],
      gapDetection: mode === 'daily' ? 'daily' : 'weekly',
      xAxisLabel: mode === 'daily' ? 'Date' : 'Week',
      yAxisLabel: 'Weight (lbs)',
      yAxisBeginAtZero: false,
      yAxisMinRange: 30,
      showLegend: false,
      showTooltips: true,
      customTooltip: (dataPoint: ChartDataPoint) => {
        const value = dataPoint.value !== null ? `${dataPoint.value} lbs` : 'No data';
        const date = dataPoint.metadata?.date || dataPoint.label;

        if (mode === 'weekly' && dataPoint.metadata?.week_key) {
          const lines = [
            `Week: ${dataPoint.metadata.week_key}`,
            `Average: ${value}`
          ];

          // Add week change if available
          if (dataPoint.metadata?.week_change !== null && dataPoint.metadata?.week_change !== undefined) {
            const change = dataPoint.metadata.week_change;
            const changeStr = change > 0 ? `+${change}` : `${change}`;
            lines.push(`Change: ${changeStr} lbs`);
          }

          // Add status indicator
          if (dataPoint.metadata?.week_status) {
            const statusMap: Record<string, string> = {
              green: '✅ Weight Loss/Stable',
              red: '❌ Weight Gain',
              baseline: '📊 Baseline Week',
              missing: '⚠️ No Data'
            };
            lines.push(statusMap[dataPoint.metadata.week_status] || dataPoint.metadata.week_status);
          }

          lines.push(`Date: ${date}`);
          return lines;
        }

        // Daily mode tooltip
        const lines = [
          `Date: ${date}`,
          `Weight: ${value}`
        ];

        // Add daily change if available
        if (dataPoint.metadata?.daily_change !== null && dataPoint.metadata?.daily_change !== undefined) {
          const change = dataPoint.metadata.daily_change;
          const changeStr = change > 0 ? `+${change.toFixed(1)}` : `${change.toFixed(1)}`;
          lines.push(`Change: ${changeStr} lbs`);

          // Add status indicator
          if (dataPoint.metadata?.daily_status) {
            const statusIcon = dataPoint.metadata.daily_status === 'green' ? '✅' : '❌';
            const statusText = dataPoint.metadata.daily_status === 'green' ? 'Loss/Stable' : 'Gain';
            lines.push(`${statusIcon} ${statusText}`);
          }
        }

        return lines;
      },
      loading,
      error,
      emptyMessage: 'No weight data available for this date range',
      height: 400,
      responsive: true,
      maintainAspectRatio: false
    };
  };

  const stats = calculateStats();

  if (!isOpen) return null;

  return (
    <div className="weight-graph-modal">
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-content">
        <div className="modal-header">
          <h2>
            <TrendingDown className="w-6 h-6" />
            {mode === 'daily' ? 'Weight Over Time' : 'Weekly Average Weight'} - {clientName}
          </h2>
          <button onClick={onClose} className="close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body">
          {/* Date Range Selector */}
          <div className="date-range-selector">
            <div className="range-buttons">
              {/* Daily mode: shorter timeframes (7, 14, 30, 90 days) */}
              {mode === 'daily' && (
                <>
                  <button
                    className={`range-btn ${dateRange === '7' ? 'active' : ''}`}
                    onClick={() => setDateRange('7')}
                  >
                    Last 7 Days
                  </button>
                  <button
                    className={`range-btn ${dateRange === '14' ? 'active' : ''}`}
                    onClick={() => setDateRange('14')}
                  >
                    Last 14 Days
                  </button>
                  <button
                    className={`range-btn ${dateRange === '30' ? 'active' : ''}`}
                    onClick={() => setDateRange('30')}
                  >
                    Last 30 Days
                  </button>
                  <button
                    className={`range-btn ${dateRange === '90' ? 'active' : ''}`}
                    onClick={() => setDateRange('90')}
                  >
                    Last 90 Days
                  </button>
                </>
              )}

              {/* Weekly mode: longer timeframes (30, 90, 180, 365 days) */}
              {mode === 'weekly' && (
                <>
                  <button
                    className={`range-btn ${dateRange === '30' ? 'active' : ''}`}
                    onClick={() => setDateRange('30')}
                  >
                    Last 30 Days
                  </button>
                  <button
                    className={`range-btn ${dateRange === '90' ? 'active' : ''}`}
                    onClick={() => setDateRange('90')}
                  >
                    Last 90 Days
                  </button>
                  <button
                    className={`range-btn ${dateRange === '180' ? 'active' : ''}`}
                    onClick={() => setDateRange('180')}
                  >
                    Last 6 Months
                  </button>
                  <button
                    className={`range-btn ${dateRange === '365' ? 'active' : ''}`}
                    onClick={() => setDateRange('365')}
                  >
                    Last 12 Months
                  </button>
                </>
              )}

              {/* Common buttons for both modes */}
              <button
                className={`range-btn ${dateRange === 'all' ? 'active' : ''}`}
                onClick={() => setDateRange('all')}
              >
                All Time
              </button>
              <button
                className={`range-btn ${dateRange === 'custom' ? 'active' : ''}`}
                onClick={() => setDateRange('custom')}
              >
                <Calendar className="w-4 h-4" />
                Custom Range
              </button>
            </div>

            {dateRange === 'custom' && (
              <div className="custom-date-inputs">
                <div className="date-input-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>
                <div className="date-input-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Chart Area - Using MetricChart! */}
          <div className="chart-area">
            <MetricChart config={buildChartConfig()} />
          </div>

          {/* Statistics Footer */}
          {stats && (
            <div className="stats-footer">
              <div className="stat-item">
                <span className="label">Starting Weight</span>
                <span className="value">{stats.startWeight.toFixed(1)} lbs</span>
              </div>
              <div className="stat-item">
                <span className="label">Current Weight</span>
                <span className="value">{stats.currentWeight.toFixed(1)} lbs</span>
              </div>
              <div className="stat-item">
                <span className="label">Total Change</span>
                <span className={`value ${stats.totalChange < 0 ? 'positive' : 'negative'}`}>
                  {stats.totalChange > 0 ? '+' : ''}{stats.totalChange.toFixed(1)} lbs
                </span>
              </div>
              <div className="stat-item">
                <span className="label">Range</span>
                <span className="value">
                  {stats.minWeight.toFixed(1)} - {stats.maxWeight.toFixed(1)} lbs
                </span>
              </div>
              <div className="stat-item">
                <span className="label">Data Points</span>
                <span className="value">{stats.dataPoints}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .weight-graph-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(4px);
    z-index: 1;
  }

  .modal-content {
    position: relative;
    width: 95vw;
    max-width: 1200px;
    max-height: 90vh;
    background: var(--bg-primary);
    border-radius: var(--radius-large);
    border: 1px solid var(--border-primary);
    box-shadow: var(--shadow-heavy);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 2;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    border-bottom: 1px solid var(--border-primary);
    background: var(--bg-primary);
  }

  .modal-header h2 {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .close-btn {
    padding: 8px;
    background: none;
    border: none;
    border-radius: var(--radius-small);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .close-btn:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .date-range-selector {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .range-buttons {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .range-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-medium);
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .range-btn:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-secondary);
  }

  .range-btn.active {
    background: var(--accent-blue);
    color: white;
    border-color: var(--accent-blue);
  }

  .custom-date-inputs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }

  .date-input-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .date-input-group label {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .date-input-group input {
    padding: 10px 12px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-medium);
    font-size: 14px;
    transition: all 0.2s ease;
  }

  .date-input-group input:focus {
    outline: none;
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .chart-area {
    flex: 1;
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .stats-footer {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
    padding: 20px;
    background: var(--bg-secondary);
    border-radius: var(--radius-medium);
    border: 1px solid var(--border-primary);
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .stat-item .label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .stat-item .value {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .stat-item .value.positive {
    color: var(--success);
  }

  .stat-item .value.negative {
    color: var(--danger);
  }

  @media (max-width: 768px) {
    .modal-content {
      width: 98vw;
      max-height: 95vh;
    }

    .modal-header,
    .modal-body {
      padding: 16px;
    }

    .range-buttons {
      flex-direction: column;
    }

    .stats-footer {
      grid-template-columns: 1fr;
    }
  }
`;
